Controller.$inject = ['$element', '$scope', '$parse', '$timeout', '$transclude', '$compile', '$filter'];

function Controller($element, $scope, $parse, $timeout, $transclude, $compile, $filter) {
    let self = this;

    /**
     * Helper function to trigger native change event for validation
     */
    const triggerChangeEvent = (inputElement) => {
        if (inputElement) {
            const changeEvent = new Event('change', {
                bubbles: true,
                cancelable: true
            });
            inputElement.dispatchEvent(changeEvent);
        }
    };
    
    self.showLegend = false;
    self.inputFocused = false;
    self.isOpen = false;
    self.listboxId = 'hui-listbox-' + Math.random().toString(36).slice(2);
    self.displayItems = [];
    self.filteredItems = [];
    self.selectedItems = [];
    self.menuItems = [];
    self.optionTemplate = null;
    self.transcludeTimeout = null;
    self.searchText = '';
    self.sortField = 'title';
    self.sortDirection = 'asc';
    self.originalItems = [];
    self.menuElement = null;
    self.maxDisplayTags = 2; // Maximum number of tags to display before showing count
    self.displayedTags = [];
    self.remainingCount = 0;
    self.$transclude = $transclude; // Make transclude available in template scope
    self.hasCustomTemplate = false;
    self.isAllSelected = false;
    self.searchDebounceTimer = null;
    self.currentSearchRequest = null;

    self.isOptionDisabled = function(item) {
        return !!(item && item.disabled === true);
    };

    self.handleFocus = () => {
        if (self.labelVariant === 'outlined') {
            self.showLegend = true;
            self.inputFocused = true;
        }
    };

    const detachMenu = () => {
        if (self.menuElm) {
            self.menuElm.style.display = 'none';
        }
        document.removeEventListener('click', handler);
    };

    self.handleBlur = () => {
        self.inputFocused = false;
        if (!self.isAllSelected && ((Array.isArray(self.ngModel) && !self.ngModel?.length)
            || (!Array.isArray(self.ngModel) && !self.ngModel))) {
            self.showLegend = false;
            if (self.keepPlaceholder && self.placeholder) {
                self.showLegend = true;
            }
        }
    };

    self.getDisplayedItems = () => {
        const inputElement = $element[0].querySelector('input[type="text"]');
        if (self.isAllSelected) {
            return $filter('translate')('All Selected');
        }

        if (self.ngModel && ((Array.isArray(self.ngModel) && self.ngModel.length) || (!Array.isArray(self.ngModel) && self.ngModel))) {
            inputElement.classList.remove('placeholder');
            if (self.labelVariant === 'outlined') {
                self.showLegend = true;
            }
            if (Array.isArray(self.ngModel)) {
                return self.ngModel
                    .map(item => {
                        return self.textKey ? item[self.textKey] : item.title || item;
                    }).join(', ');
            } else {
                return self.textKey ? self.ngModel[self.textKey] : self.ngModel.title || self.ngModel;
            }
        } else {
            if (self.labelVariant === 'outlined') {
                self.showLegend = !!(self.keepPlaceholder && self.placeholder);
            }
            inputElement.classList.add('placeholder');
            return '';
        }
    };

    self.sortItems = (field = self.sortField, direction = self.sortDirection) => {
        self.sortField = field;
        self.sortDirection = direction;
        let itemsToSort = [...self.displayItems];

        // Apply search filter first (if applicable and not paginated)
        if (!self.isPaginated) {
            if (self.searchText && self.searchText.trim()) {
                const searchLower = self.searchText.toLowerCase().trim();
                itemsToSort = itemsToSort.filter(item => {
                    const title = self.textKey ? item[self.textKey] : (item.title || item);
                    if (typeof title === 'string') {
                        return title.toLowerCase().includes(searchLower);
                    }
                    const value = item.value ? item.value.toString().toLowerCase() : JSON.stringify(item).toLowerCase();
                    return value.includes(searchLower);
                });
            }
        }

        if (itemsToSort.length === 0 || direction === 'none') {
            self.filteredItems = itemsToSort;
            return;
        }

        itemsToSort.sort((a, b) => {
            let valueA, valueB;

            if (field === 'title') {
                valueA = self.textKey ? a[self.textKey] : (a.title || a);
                valueB = self.textKey ? b[self.textKey] : (b.title || b);
            } else if (field === 'value') {
                valueA = a.value;
                valueB = b.value;
            } else {
                valueA = a[field];
                valueB = b[field];
            }

            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return direction === 'asc'
                    ? valueA.localeCompare(valueB)
                    : valueB.localeCompare(valueA);
            } else {
                if (valueA === undefined) valueA = '';
                if (valueB === undefined) valueB = '';

                return direction === 'asc'
                    ? (valueA < valueB ? -1 : valueA > valueB ? 1 : 0)
                    : (valueA > valueB ? -1 : valueA < valueB ? 1 : 0);
            }
        });

        self.filteredItems = itemsToSort;

        if (self.hasCustomTemplate) {
            if (self.transcludeTimeout) {
                $timeout.cancel(self.transcludeTimeout);
            }

            self.transcludeTimeout = $timeout(() => {
                self.filteredItems.forEach((item, index) => {
                    self.transcludeTemplateFor(item, index);
                });
            }); // delay a bit to wait for DOM + debounce keystrokes
        }

        $scope.$applyAsync(() => {
            if (self.hasCustomTemplate && typeof self.scheduleTransclusion === 'function') {
                self.scheduleTransclusion();
            }
        });
    };

    self.filterItems = () => {
        if (self.searchDebounceTimer) {
            $timeout.cancel(self.searchDebounceTimer);
        }

        if (self.currentSearchRequest) {
            self.currentSearchRequest.cancel();
            self.currentSearchRequest = null;
        }

        self.searchDebounceTimer = $timeout(() => {
            self.performSearch();
        }, 300);
    };

    self.performSearch = () => {
        if (self.isPaginated) {
            self.isSearching = true;
            var onEvent = function (event) {
                switch (event) {
                    case 'start':
                        self.isSearching = true;
                        break;
                    case 'end':
                        self.isSearching = false;
                        break;
                }

                $scope.$evalAsync();
            };
            var $event = {value: self.searchText, on: onEvent};

            var returnMap = {};
            const searchRequest = {
                promise: undefined,
                cancelled: false,
                cancel: () => {
                    searchRequest.cancelled = true;
                    self.isSearching = false;
                }
            };

            self.currentSearchRequest = searchRequest;

            var promise = self.resultCallback({$event: $event, returnMap: returnMap}) || returnMap['promise'];

            if (promise && typeof promise.then === 'function') {
                promise
                    .then(function (items) {
                        if (!searchRequest.cancelled && self.currentSearchRequest === searchRequest) {
                            self.displayItems = items;
                            self.filteredItems = items;
                            self.sortItems(self.sortField, self.sortDirection);
                            $scope.$evalAsync(() => {
                                if (self.hasCustomTemplate && typeof self.scheduleTransclusion === 'function') {
                                    self.scheduleTransclusion();
                                }
                            });
                        }
                    }, function (error) {
                        if (error && error.code !== 'MIN_LENGTH') {
                            console.error(error);
                        } else {
                            // Fallback to original list when minimum length not met
                            self.filteredItems = [...self.displayItems];
                            $scope.$evalAsync(() => {
                                if (self.hasCustomTemplate && typeof self.scheduleTransclusion === 'function') {
                                    self.scheduleTransclusion();
                                }
                            });
                        }
                    });
            }
        } else {
            self.sortItems(self.sortField, self.sortDirection);
        }
    };

    self.onSearchChange = () => {
        self.filterItems();
        $scope.$applyAsync();
    };

    self.clearSearch = () => {
        self.searchText = '';

        if (self.searchDebounceTimer) {
            $timeout.cancel(self.searchDebounceTimer);
            self.searchDebounceTimer = null;
        }

        if (self.currentSearchRequest) {
            self.currentSearchRequest.cancel();
            self.currentSearchRequest = null;
        }

        if (self.isPaginated) {
            self.filteredItems = [...self.displayItems];
            $scope.$evalAsync(() => {
                if (self.hasCustomTemplate && typeof self.scheduleTransclusion === 'function') {
                    self.scheduleTransclusion();
                }
            });
        } else {
            self.filterItems();
        }
        $timeout(() => {
            const searchInput = self.menuElm ? self.menuElm.querySelector('.hui-select-search-input') : null;
            if (searchInput) {
                searchInput.focus();
            }
        }, 100);
    };

    // Reusable transclusion scheduler that waits for DOM placeholders to appear
    self.scheduleTransclusion = () => {
        if (!self.hasCustomTemplate) return;
        const container = $element[0];

        if (self.transcludeObserver) {
            try { self.transcludeObserver.disconnect(); } catch (e) {}
            self.transcludeObserver = null;
        }

        const tryTransclude = () => {
            $timeout(() => {
                self.filteredItems.forEach((item, index) => {
                    self.transcludeTemplateFor(item, index);
                });
            });
        };

        const placeholders = container.querySelectorAll('[id^="menu-item-"]');
        if (placeholders.length >= self.filteredItems.length) {
            tryTransclude();
            return;
        }

        self.transcludeObserver = new MutationObserver(() => {
            const nodes = container.querySelectorAll('[id^="menu-item-"]');
            if (nodes.length >= self.filteredItems.length) {
                tryTransclude();
                if (self.transcludeObserver) {
                    self.transcludeObserver.disconnect();
                    self.transcludeObserver = null;
                }
            }
        });

        self.transcludeObserver.observe(container, { childList: true, subtree: true });
    };

    self.resetFilters = () => {
        self.searchText = '';
        if (self.searchDebounceTimer) {
            $timeout.cancel(self.searchDebounceTimer);
            self.searchDebounceTimer = null;
        }

        if (self.currentSearchRequest) {
            self.currentSearchRequest.cancel();
            self.currentSearchRequest = null;
        }

        if (!self.sortConfig) {
            self.sortDirection = 'none';
            self.filteredItems = [...self.displayItems];
        } else {
            self.sortItems(self.sortConfig.field, self.sortConfig.direction);
        }
        $scope.$applyAsync();
    };

    self.processItems = () => {
        let items = null;
        if (self.itemsExpression) {
            const match = self.itemsExpression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)(?:\s+track\s+by\s+([\s\S]+?))?\s*$/);
            let collectionExpr = self.itemsExpression;
            if (match) {
                collectionExpr = match[2];
            }
            const getter = $parse(collectionExpr);
            try {
                items = getter($scope.$parent);
            } catch (e) {
                // ignore parse errors here; watcher below will also try again
            }
        }

        if (!items && Array.isArray(self.items)) {
            items = self.items;
        }

        if (items && Array.isArray(items)) {
            self.displayItems = items;
            self.originalItems = [...items];
            self.filteredItems = [...items];

            if (!self.ngModel) {
                self.ngModel = self.multiSelect ? [] : null;
            }

            if (self.sortConfig) {
                self.sortItems(self.sortConfig.field || 'title', self.sortConfig.direction || 'asc');
            }

            self.updateSelectedItems();
        }
    };

    self.updateSelectedItems = () => {
        if (!self.isAllSelected) {
            if (Array.isArray(self.ngModel)) {
                self.selectedItems = self.ngModel;
            } else if (self.ngModel) {
                self.selectedItems = [self.ngModel];
            } else {
                self.selectedItems = [];
            }
        }

        const displayText = self.getDisplayedItems();
        const inputElement = $element[0].querySelector('input[type="text"]');
        if (inputElement) {
            inputElement.value = displayText;
        }
    };

    self.selectItem = (item, $event) => {
        if (self.isOptionDisabled(item)) {
            if ($event) {
                $event.preventDefault();
                $event.stopPropagation();
            }
            return;
        }
        if (self.multiSelect) {
            self.isAllSelected = false;

            const index = self.selectedItems.findIndex(selected => {
                if (self.idKey) {
                    return selected && item && selected[self.idKey] === item[self.idKey];
                }
                return selected === item;
            });

            if (index > -1) {
                self.selectedItems.splice(index, 1);
            } else {
                self.selectedItems.push(item);
            }

            self.ngModel = [...self.selectedItems];

            if ($event) {
                $event.stopPropagation();
            }
        } else {
            self.ngModel = item;
            self.toggleMenu();
        }

        self.updateSelectedItems();

        if (self.onChange) {
            $timeout(() => {
                self.onChange({$event: {target: {value: self.ngModel}}});
            });
        }
        $timeout(() => {
            const inputElement = $element[0].querySelector('input[type="text"]');
            triggerChangeEvent(inputElement);
        }, 0);
    };

    self.isSelected = (item) => {
        if (self.isAllSelected && Array.isArray(this.selectedItems)) {
            return this.selectedItems.some(selected =>
                selected[self.idKey] === item[self.idKey]);
        }

        if (Array.isArray(self.ngModel)) {
            if (self.idKey) {
                return self.ngModel.some(selected => selected && item && selected[self.idKey] === item[self.idKey]);
            }
            return self.ngModel.some(selected => selected === item);
        }

        if (!self.ngModel) {
            return false;
        }
        return self.idKey ? (self.ngModel[self.idKey] === item[self.idKey]) : (self.ngModel === item);
    };

    self.toggleMenu = () => {
        const toggleElm = $element[0].querySelector('input[type="text"]');
        self.menuElm = $element[0].querySelector('#hui-select-menu-' + self.hName);

        if (!self.menuElm) {
            let parent = $element[0].parentElement;
            while (parent) {
                const foundMenu = parent.querySelector('#hui-select-menu-' + self.hName);
                if (foundMenu) {
                    self.menuElm = foundMenu;
                    break;
                }
                parent = parent.parentElement;
            }
        }

        if (self.menuElm && toggleElm) {
            if (self.isOpen) {
                detachMenu();
                self.isOpen = false;
            } else {
                self.isOpen = true;
                self.menuElm.style.display = 'block';
                self.menuElm.style.position = 'absolute';
                self.menuElm.style.top = '0px';
                self.menuElm.style.left = '0px';
                const hostElm = self.appendToDropdown
                    ? (document.querySelector(self.appendToDropdown) || document.body)
                    : document.body;

                try {
                    const boundariesElement = hostElm === document.body ? 'viewport' : hostElm;
                    const options = {
                        placement: self.menuPlacement || 'bottom-start',
                        removeOnDestroy: true,
                        modifiers: {
                            preventOverflow: { boundariesElement: boundariesElement },
                            flip: { enabled: !!self.enableFlip, boundariesElement: boundariesElement }
                        }
                    };

                    if (self.menuElm.parentElement !== hostElm) {
                        hostElm.appendChild(self.menuElm);
                    }

                    self.popper = new Popper(toggleElm, self.menuElm, options);
                    document.addEventListener('click', handler);

                    if (self.searchable) {
                        $timeout(() => {
                            const searchInput = self.menuElm.querySelector('.hui-select-search-input');
                            if (searchInput) {
                                searchInput.focus();
                            }
                        }, 100);
                    }
                } catch (e) {
                    console.error('Popper failed to initialize', e);
                }
            }
        }
    };

    const handler = (event) => {
        if (event.target.classList.contains('hui-select-search-input') ||
            event.target.classList.contains('search-clear-icon') ||
            event.target.closest('.hui-select-sort-controls')) {
            return;
        }

        if (self.isOpen &&
            !self.menuElm?.contains(event.target) &&
            !$element[0]?.contains(event.target)) {
            self.toggleMenu();
            $scope.$applyAsync();
        }
    };

    self.$onInit = () => {
        self.multiSelect = self.multiSelect || false;
        self.searchable = !!self.searchable;
        self.sortDirection = 'asc';
        self.sortConfig = self.sortConfig || { field: 'title', direction: 'asc' };
        self.maxDisplayTags = self.maxDisplayTags || 2; // Default to 2 if not specified
        self.hasCustomTemplate = $transclude.isSlotFilled('itemTemplate');
        self.hasSelectedItems = !!$transclude.isSlotFilled('selectedItem');
        self.processItems();
    };

    self.transcludeTemplateFor = function (item, index) {
        const itemScope = $scope.$new(false);
        itemScope.item = item;
        itemScope.$inputSelectCtrl = self; // Provide access to the controller
        
        itemScope.handleClick = function (event) {
            if (typeof item.onClick === 'function') {
                item.onClick(item, event);
            }
        };

        const key = self.idKey ? (item && item[self.idKey]) : index;
        const searchRoot = self.menuElm || $element[0];
        const id = `menu-item-${key}`;
        const target = searchRoot.querySelector(`[id="${id}"]`);
        if (!target) return;

        const container = angular.element(target);

        // Clear any existing content to prevent duplication
        container.empty();

        if (self.hasCustomTemplate) {
              $transclude(itemScope, clone => {
                  const compiled = $compile(clone)(itemScope);
                  container.append(compiled);
            }, null, 'itemTemplate');
        }
    };

    self.$postLink = () => {
        const inputElement = $element[0].querySelector('input[type="text"]');

        if (!self.ngModel || (Array.isArray(self.ngModel) && !self.ngModel.length)) {
            inputElement.classList.add('placeholder');
            self.showLegend = false;
            if (self.keepPlaceholder && self.placeholder) {
                self.showLegend = true;
            }
        } else {
            if (self.labelVariant === 'outlined') {
                self.showLegend = true;
            }
            inputElement.classList.remove('placeholder');
            inputElement.value = self.getDisplayedItems();
        }

        $timeout(() => {
            self.processItems();

            if (!self.hasCustomTemplate) return;

            const container = $element[0]; // Parent element to observe

            const observer = new MutationObserver((mutationsList, observerInstance) => {
                const targetNodes = container.querySelectorAll('[id^="menu-item-"]');

                if (targetNodes.length === self.filteredItems.length) {
                    // Use $timeout to ensure this runs in the Angular digest cycle
                    $timeout(() => {
                        self.filteredItems.forEach((item, index) => {
                            self.transcludeTemplateFor(item, index);
                        });
                    });

                    // Stop observing once all items are present
                    observerInstance.disconnect();
                }
            });

            // Start observing for changes to child elements
            observer.observe(container, {
                childList: true,
                subtree: true
            });
        }, 0);
    };

    self.handleChange = ($event) => {
        const inputElement = $element[0].querySelector('input[type="text"]');
        inputElement.value = self.getDisplayedItems() ? self.getDisplayedItems() : '';
        if (self.onChange) {
            self.onChange({$event});
        }
        triggerChangeEvent(inputElement);
    };

    self.handleSearchKeydown = (event) => {
        event.stopPropagation();

        if (event.keyCode === 27) {
            self.toggleMenu();
            $scope.$applyAsync();
        }
    };


    self.clearAll = ($event) => {
        if ($event) {
            $event.preventDefault();
            $event.stopPropagation();
        }
        if (self.multiSelect) {
            self.isAllSelected = false;
            const currentSelections = Array.isArray(self.ngModel) ? self.ngModel : [];
            const preservedDisabledSelections = currentSelections.filter(item => self.isOptionDisabled(item));
            const allItemIndex = self.originalItems.findIndex(item =>
                 item[self.idKey]?.toLowerCase() === 'all'
            );

            if (allItemIndex > -1 && !preservedDisabledSelections.length) {
                self.ngModel = []; // Assuming "All" is the first item
                self.selectedItems = [self.originalItems[allItemIndex]];
                self.isAllSelected = true;
                self.showLegend = true;
            } else {
                self.ngModel = [...preservedDisabledSelections];
                self.selectedItems = [...preservedDisabledSelections];
            }

        } else {
            const keepCurrent = self.ngModel && self.isOptionDisabled(self.ngModel) ? self.ngModel : null;
            self.selectedItems = keepCurrent ? [keepCurrent] : [];
            self.ngModel = keepCurrent;
        }
        self.updateSelectedItems();
        if (self.onChange) {
            $timeout(() => {
                self.onChange({$event: {target: {value: self.ngModel}}});
            });
        }
        $timeout(() => {
            const inputElement = $element[0].querySelector('input[type="text"]');
            triggerChangeEvent(inputElement);
        }, 0);
    };

    self.selectAll = ($event) => {
        if ($event) {
            $event.preventDefault();
            $event.stopPropagation();
        }

        self.filteredItems.forEach(item => {
            if (!self.isOptionDisabled(item) && !self.isSelected(item)) {
                self.selectItem(item, null, true);
            }
        });

        if (self.noneOnAll) {
            self.isAllSelected = true;
            self.showLegend = true;
            this.ngModel = [];
        }

        self.updateSelectedItems();
        if (self.onChange) {
            $timeout(() => {
                self.onChange({$event: {target: {value: self.ngModel}}});
            });
        }
        $timeout(() => {
            const inputElement = $element[0].querySelector('input[type="text"]');
            triggerChangeEvent(inputElement);
        }, 0);
    };

    self.getSelectedItemTemplate = () => {
        if (!self.ngModel) return '';

        if (self.multiSelect) {
            return self.ngModel.map(item => self.textKey ? item[self.textKey] : item.title || item).join(', ');
        }

        return self.textKey ? self.ngModel[self.textKey] : self.ngModel.title || self.ngModel;
    };


    self.handleSelectDisplayClick = ($event) => {
        const target = $event.target;
        if (target.classList.contains('fa-times')) {
            const removeIcon = target.closest('i.fa-times');
            const badgeElement = removeIcon ? removeIcon.closest('.hui-badge') : null;

            if (badgeElement) {
                const itemId = badgeElement.getAttribute('data-item-id');
                if (itemId) {
                    const itemIndex = self.selectedItems.findIndex(function(item) {
                        return String(item.id) === String(itemId);
                    });

                    if (itemIndex > -1) {
                        self.selectedItems.splice(itemIndex, 1);
                        self.ngModel = angular.copy(self.selectedItems);

                        self.updateSelectedItems();
                        if (self.onModelChange) {
                            self.onModelChange(self.ngModel);
                        }
                        if (self.onChange) {
                            self.onChange({ $event: self.ngModel });
                        }
                        return;
                    }
                }
            }
            return;
        }

        self.toggleMenu($event);
    };


    $scope.$watch(() => self.ngModel, (newValue, oldValue) => {
        if (newValue !== oldValue) {
            self.updateSelectedItems();
            self.handleChange();
        }
    }, true);

    $scope.$watch(() => self.items, (newValue, oldValue) => {
        if (newValue !== oldValue) {
            self.processItems();
        }
    }, true);

    $scope.$watch(() => self.searchText, (newValue, oldValue) => {
        if (newValue !== oldValue) {
            self.filterItems();
        }
    });

    $scope.$watch(() => self.sortConfig, (newConfig) => {
        if (newConfig) {
            self.sortItems(newConfig.field || 'title', newConfig.direction || 'asc');
        }
    });
     self.isLoadingMore = false;

     self.handleLoadMore =  async () => {
        try {
            if (!self.isPaginated || self.isLoadingMore) {
                return;
            }

            self.isLoadingMore = true;
            let loadPromise = {};
            const promise = await self.onReachBottom({'loadPromise': loadPromise}) || loadPromise['promise'];
            await promise;
            self.isLoadingMore = false;
            $scope.$evalAsync();
        } catch (err) {
            console.error(err);
            self.isLoadingMore = false;
            $scope.$evalAsync();
        }
    };

    // Also watch itemsExpression-driven collections (string expressions and "item in" syntax)
    $scope.$watch(() => {
        if (self.itemsExpression) {
            const match = self.itemsExpression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)(?:\s+track\s+by\s+([\s\S]+?))?\s*$/);
            let collectionExpr = self.itemsExpression;
            if (match) {
                collectionExpr = match[2];
            }
            try {
                const getter = $parse(collectionExpr);
                return getter($scope.$parent);
            } catch (e) {
                return undefined;
            }
        }
        return undefined;
    }, (newVal, oldVal) => {
        if (newVal !== oldVal) {
            self.processItems();
        }
    }, true);

    // Watch bgColor to update CSS variable on the input element
    $scope.$watch(() => self.bgColor, (newColor) => {
        const inputEl = $element[0].querySelector('input[type="text"]');
        if (!inputEl) return;
        if (newColor) {
            inputEl.style.setProperty('--bg-color', `var(${newColor})`);
        } else {
            inputEl.style.removeProperty('--bg-color');
        }
    });

    self.$onDestroy = () => {
        detachMenu();

        if (self.searchDebounceTimer) {
            $timeout.cancel(self.searchDebounceTimer);
            self.searchDebounceTimer = null;
        }

        if (self.currentSearchRequest) {
            self.currentSearchRequest.cancel();
            self.currentSearchRequest = null;
        }

        if (self.popper) {
            self.popper.destroy();
        }
        self.popper = null;
    };
}

export function HuiInputSelect() {
    return {
        restrict: 'E',
        scope: true,
        bindToController: {
            labelVariant: '@',
            iconPosition: '@',
            ngModel: '=',
            placeholder: '@',
            hName: '@',
            disabled: '<',
            onChange: '&',
            textKey: '@',
            idKey: '@',
            items: '@',
            multiSelect: '<',
            isLoading: '<',
            fullWidth: '<',
            menuPlacement: '@',
            searchable: '<?',
            enableFlip: '<?',
            sortConfig: '<?',
            showSelectAllOptions: '<?',
            onReachBottom: '&',
            isPaginated: '<?',
            resultCallback: '&',
            appendToDropdown: '@',
            hasError: '<',
            bgColor: '@',
            noneOnAll: '<?',
            keepPlaceholder: '<?'
        },
        controller: Controller,
        controllerAs: '$inputSelectCtrl',
        transclude: {
            label: '?huiInputLabel',
            icon: '?huiInputIcon',
            itemTemplate: '?huiItemTemplate',
            selectedItem: '?huiSelectedItem'
        },
        compile: function(tElement) {
            return {
                pre: function(scope, element, attrs, ctrl) {
                    if (attrs.items) {
                        ctrl.itemsExpression = attrs.items;
                    }
                }
            };
        },
        template: () => {
            return `
                <hui-input-base
                    label-variant="{{$inputSelectCtrl.labelVariant}}"
                    icon-position="{{$inputSelectCtrl.iconPosition || 'start'}}"
                    has-error="$inputSelectCtrl.hasError"
                    show-legend="$inputSelectCtrl.showLegend"
                    input-focused="$inputSelectCtrl.inputFocused"
                    model-value="$inputSelectCtrl.ngModel"
                    disabled="$inputSelectCtrl.disabled"
                    full-width="$inputSelectCtrl.fullWidth">
                    <hui-input-base-label ng-transclude ng-transclude-slot="label"></hui-input-base-label>
                    <hui-input-base-icon ng-transclude ng-transclude-slot="icon"></hui-input-base-icon>
                    
                   <div class="hui-select-wrapper">
                        <input type="hidden" 
                               ng-model="$inputSelectCtrl.ngModel" />
                        <input type="text" 
                               ng-click="$inputSelectCtrl.toggleMenu()"
                               class="hui-base-input hui-input-select"
                               role="combobox"
                               aria-haspopup="listbox"
                               ng-attr-aria-expanded="{{$inputSelectCtrl.isOpen}}"
                               ng-attr-aria-controls="{{$inputSelectCtrl.listboxId}}"
                               ng-attr-aria-disabled="{{$inputSelectCtrl.disabled}}"
                               ng-class="{'placeholder': !$inputSelectCtrl.ngModel || ($inputSelectCtrl.multiSelect && !$inputSelectCtrl.ngModel.length) || $inputSelectCtrl.isAllSelected 
                                                        || ($inputSelectCtrl.selectedItems.length === $inputSelectCtrl.originalItems.length && $inputSelectCtrl.originalItems.length > 0),
                                                                                    'with-badges': $inputSelectCtrl.hasSelectedItems && $inputSelectCtrl.ngModel && (!$inputSelectCtrl.multiSelect || $inputSelectCtrl.ngModel.length)}"
          
                               ng-style="{'color': ($inputSelectCtrl.ngModel && (!$inputSelectCtrl.multiSelect || $inputSelectCtrl.ngModel.length)) ? 'transparent' : ''}"
                               readonly
                               ng-disabled="$inputSelectCtrl.disabled"
                               name="{{$inputSelectCtrl.hName}}"
                               placeholder="{{($inputSelectCtrl.labelVariant !== 'outlined' || ($inputSelectCtrl.keepPlaceholder && $inputSelectCtrl.placeholder)) ? $inputSelectCtrl.placeholder : ''}}"                               ng-focus="$inputSelectCtrl.handleFocus()"
                               ng-blur="$inputSelectCtrl.handleBlur()"
                        />
                       
                        <div class="hui-select-display" 
                             ng-click="$inputSelectCtrl.handleSelectDisplayClick($event)"
                             ng-class="{'has-tags': $inputSelectCtrl.remainingCount > 0, 'badges-display':$inputSelectCtrl.hasSelectedItems}"
                             ng-if="$inputSelectCtrl.ngModel && (!$inputSelectCtrl.multiSelect || $inputSelectCtrl.ngModel.length) && !$inputSelectCtrl.disabled">
                             
                             <div ng-if="$inputSelectCtrl.$transclude.isSlotFilled('selectedItem')" class="badges-items">
                                  <ng-transclude ng-transclude-slot="selectedItem"></ng-transclude>
                             </div>
                                  
                             <div ng-if="!$inputSelectCtrl.$transclude.isSlotFilled('selectedItem')"
                                  class="hui-selected-item">
                                   <div ng-if="$inputSelectCtrl.multiSelect" 
                                        class="hui-selected-display">
                                        <div ng-if="$inputSelectCtrl.isAllSelected || ($inputSelectCtrl.selectedItems.length === $inputSelectCtrl.originalItems.length && $inputSelectCtrl.originalItems.length > 0)"
                                                 class="hui-selected-display">
                                                <span class="placeholder">All Selected</span>
                                        </div>
                                        <span ng-if="!($inputSelectCtrl.isAllSelected || ($inputSelectCtrl.selectedItems.length === $inputSelectCtrl.originalItems.length && $inputSelectCtrl.originalItems.length > 0))">
                                            <span ng-repeat="item in $inputSelectCtrl.ngModel">
                                                 {{ $inputSelectCtrl.textKey ? item[$inputSelectCtrl.textKey] : (item.title || item) }}<span ng-if="!$last">, </span>
                                            </span>
                                        </span>  
                                   </div>
                                   <div ng-if="!$inputSelectCtrl.multiSelect"
                                        class="d-flex align-items-center gap-1 hui-selected-display">
                                        <span>{{ $inputSelectCtrl.textKey ? $inputSelectCtrl.ngModel[$inputSelectCtrl.textKey] : ($inputSelectCtrl.ngModel.title || $inputSelectCtrl.ngModel) }}</span>
                                   </div>
                             </div>   
                        </div>
                   </div>
            
                  <i class="fa hui-select-arrow text-primary" 
                     ng-class="{'fa-chevron-down': !$inputSelectCtrl.isOpen && !$inputSelectCtrl.isLoading,
                                'fa-chevron-up': $inputSelectCtrl.isOpen && !$inputSelectCtrl.isLoading,
                                'icon-disabled': $inputSelectCtrl.disabled}"
                     aria-hidden="true"></i>
                 
                  <span ng-if="$inputSelectCtrl.isLoading" 
                          class="d-flex align-items-center text-primary hui-select-arrow">
                      <i class="fa fa-circle-notch fa-spin"></i>
                  </span>
                    
                  <div class="hui-menu hui-select-menu" ng-if="$inputSelectCtrl.items && $inputSelectCtrl.items.length"
                       style="display: none;" ng-attr-id="hui-select-menu-{{$inputSelectCtrl.hName}}">
                        <div class="hui-menu-content hui-select-menu-container" role="listbox" ng-attr-id="{{$inputSelectCtrl.listboxId}}" ng-attr-aria-multiselectable="{{$inputSelectCtrl.multiSelect ? 'true' : undefined}}" h-infinite-scroll
                                                      on-reach-bottom="$inputSelectCtrl.handleLoadMore()">
                            <div class="hui-select-controls" ng-if="$inputSelectCtrl.searchable">
                                <div ng-if="$inputSelectCtrl.searchable" class="hui-select-search">
                                    <input type="text" 
                                           class="hui-base-input hui-select-search-input"
                                           ng-model="$inputSelectCtrl.searchText"
                                           ng-change="$inputSelectCtrl.onSearchChange()"
                                           ng-keydown="$inputSelectCtrl.handleSearchKeydown($event)"
                                           placeholder="Search..."
                                           autocomplete="off" />
                                    <span class="hui-input-icon icon-end">
                                          <button class="search-clear-icon" 
                                                  ng-if="$inputSelectCtrl.searchText"
                                                  ng-click="$inputSelectCtrl.clearSearch()">
                                                <span aria-hidden="true">&times;</span>
                                          </button>
                                    </span>
                                </div>
                            </div>
                        
                          <div ng-repeat="item in ($inputSelectCtrl.filteredItems | limitTo:800) track by ($inputSelectCtrl.idKey ? item[$inputSelectCtrl.idKey] : $index)"
                               class="hui-select-item">
                                 <span ng-if="$inputSelectCtrl.hasCustomTemplate" id="menu-item-{{$inputSelectCtrl.idKey ? item[$inputSelectCtrl.idKey] : $index}}"></span>
                                    
                                 <div ng-if="!$inputSelectCtrl.hasCustomTemplate" 
                                      class="hui-menu-item hui-select-option"
                                      ng-class="{'active': $inputSelectCtrl.isSelected(item)}"
                                      ng-click="$inputSelectCtrl.selectItem(item, $event)">
                                       {{item[$inputSelectCtrl.textKey || 'title']}}
                                 </div>
                            </div>
                            
                            <div class="hui-select-no-options" 
                                 ng-if="$inputSelectCtrl.filteredItems.length === 0 && $inputSelectCtrl.searchText">
                                {{ 'No results found for' | translate }} "{{$inputSelectCtrl.searchText}}"
                            </div>
                            
                            <div class="hui-select-no-options"
                                 ng-if="$inputSelectCtrl.displayItems.length === 0">
                                {{ 'No options available' | translate }} 
                            </div>
                            
                            <div class="hui-menu-footer" ng-if="$inputSelectCtrl.showSelectAllOptions">
                                <a href=""
                                   ng-click="$inputSelectCtrl.clearAll($event)"
                                   ng-if="$inputSelectCtrl.selectedItems.length > 0">
                                   {{ 'Clear All' | translate }} 
                                </a>
                                <a href=""
                                   ng-click="$inputSelectCtrl.selectAll($event)"
                                   ng-if="$inputSelectCtrl.filteredItems.length > 0 && $inputSelectCtrl.multiSelect">
                                   {{ 'Select All' | translate }} 
                                </a>
                            </div>
                        </div>
                  </div>
                </hui-input-base>
            `;
        }
    };
}